import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { auth } from '../core/auth.js';
import { t } from '../core/i18n.js';
import { showFormError, clearFormError, fieldFromApiError } from '../core/formError.js';

export class InviteView extends Component {
  constructor(ctx) {
    super();
    this.token = (ctx && ctx.params && ctx.params.token) || '';
    // `error`/`busy` handled via DOM so the form keeps its values on failure.
    this.state = {
      loading: true,
      preview: null,
      loadError: null,
    };
  }

  template() {
    if (this.state.loading) return `<section class="stack"><div class="muted">${esc(t('invite.loading'))}</div></section>`;
    if (this.state.loadError) {
      return `
        <section class="stack">
          <div class="card stack">
            <h2>${esc(t('invite.title'))}</h2>
            <div class="error-banner">${esc(this.state.loadError)}</div>
            <a class="btn btn-ghost btn-block" href="#/login">${esc(t('invite.go_to_signin'))}</a>
          </div>
        </section>
      `;
    }
    const p = this.state.preview;
    const lockedEmail = p.email !== null && p.email !== '';
    // The invited_by line names what the invite attaches to. `tenant` uses
    // the original invite.invited_by wording; unknown kinds (pre-`kind`
    // previews) fall back to it too.
    const KIND_KEYS = {
      tenant: 'invite.invited_by',
      property: 'invite.invited_by_property',
      agency: 'invite.invited_by_agency',
      generic: 'invite.invited_by_generic',
    };
    const kindKey = KIND_KEYS[p.kind] || KIND_KEYS.tenant;
    const invitedBy = t(kindKey, { inviter: p.inviter_name || t('invite.default_inviter') });
    return `
      <section class="stack">
        <div class="card stack">
          <h2>${esc(t('invite.youre_invited'))}</h2>
          <p class="faint">
            ${esc(invitedBy)}
            ${p.expires_at ? esc(t('invite.link_valid_until', { date: p.expires_at })) : ''}
          </p>
          <div data-form-error></div>
          <form class="stack" data-form>
            <div class="field">
              <label for="full_name">${esc(t('invite.full_name'))}</label>
              <input class="input" id="full_name" name="full_name" required value="${esc(p.full_name || '')}" />
            </div>
            <div class="field">
              <label for="email">${esc(t('invite.email'))}</label>
              <input class="input" id="email" name="email" type="email" autocomplete="email" required
                     value="${esc(p.email || '')}" ${lockedEmail ? 'readonly' : ''} />
              ${lockedEmail ? `<span class="faint">${esc(t('invite.email_locked'))}</span>` : ''}
            </div>
            <div class="field">
              <label for="password">${esc(t('invite.set_password'))}</label>
              <input class="input" id="password" name="password" type="password"
                     autocomplete="new-password" required minlength="8" />
            </div>
            <div class="field">
              <label for="confirm">${esc(t('invite.confirm_password'))}</label>
              <input class="input" id="confirm" name="confirm" type="password"
                     autocomplete="new-password" required minlength="8" />
            </div>
            <button class="btn btn-primary btn-block" type="submit" data-submit>
              <span data-submit-label>${esc(t('invite.accept'))}</span>
            </button>
          </form>
        </div>
      </section>
    `;
  }

  mount() {
    if (this.state.loading) {
      this._loadPreview();
      return;
    }
    const form = this.el.querySelector('[data-form]');
    if (form) form.addEventListener('submit', (e) => this._submit(e));
  }

  async _loadPreview() {
    try {
      const preview = await api.get(`/invitations/by-token/${encodeURIComponent(this.token)}`);
      this.setState({ loading: false, preview });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t('invite.errors.load');
      this.setState({ loading: false, loadError: message });
    }
  }

  _setBusy(busy) {
    const btn = this.el.querySelector('[data-submit]');
    const label = this.el.querySelector('[data-submit-label]');
    if (btn) btn.disabled = busy;
    if (label) label.textContent = busy ? t('common.loading') : t('invite.accept');
  }

  async _submit(e) {
    e.preventDefault();
    const card = this.el.querySelector('.card');
    const btn = this.el.querySelector('[data-submit]');
    if (btn && btn.disabled) return;
    const form = new FormData(e.target);
    const email = String(form.get('email') || '').trim();
    const password = String(form.get('password') || '');
    const confirm = String(form.get('confirm') || '');
    const fullName = String(form.get('full_name') || '').trim();
    clearFormError(card);
    if (password !== confirm) {
      showFormError(card, t('invite.passwords_dont_match'), { field: 'confirm' });
      return;
    }
    this._setBusy(true);
    try {
      const session = await api.post(
        `/invitations/by-token/${encodeURIComponent(this.token)}/accept`,
        { email, password, full_name: fullName }
      );
      auth.set(session);
      // Land the invitee on what they were invited to (property, agency,
      // tenancy) rather than the generic dashboard.
      window.location.hash = '#' + auth.landingPathForInvite(this.state.preview);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t('invite.errors.accept');
      const field = err instanceof ApiError
        ? fieldFromApiError(err, ['email', 'password', 'full_name'])
        : null;
      showFormError(card, message, { field });
      this._setBusy(false);
    }
  }
}
